import React from "react";
import {
  Projector,
  Flame,
  Mountain,
  UtensilsCrossed,
  Droplet,
  Cookie,
  Bed,
  Sunset,
  Plus,
} from "lucide-react";
import { AMENITIES, ALL_AMENITIES } from "@/lib/data";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const ICONS = {
  Projector,
  Campfire: Flame,
  Mountains: Mountain,
  Grill: UtensilsCrossed,
  Drop: Droplet,
  Cookie,
  BedDouble: Bed,
  SunHorizon: Sunset,
};

export default function Amenities() {
  return (
    <section id="amenities" data-testid="amenities-section" className="bg-mrn-bg py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.3em] text-mrn-terracotta font-medium">
            Amenities
          </p>
          <h2 className="mt-4 headline-display text-4xl md:text-6xl text-mrn-wood">
            Everything for <em className="text-mrn-green font-light">an unplanned weekend.</em>
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
          {AMENITIES.map((a, i) => {
            const Icon = ICONS[a.icon] || Sunset;
            return (
              <div
                key={a.title}
                data-testid={`amenity-${i}`}
                className="group bg-white/70 backdrop-blur-sm border border-mrn-sand-dark/30 rounded-2xl p-6 hover:bg-mrn-wood hover:border-mrn-wood transition-all duration-500"
              >
                <Icon
                  size={30}
                  strokeWidth={1.5}
                  className="text-mrn-terracotta group-hover:text-mrn-amber transition-colors"
                />
                <h3 className="mt-5 font-display text-2xl text-mrn-wood group-hover:text-mrn-cream">
                  {a.title}
                </h3>
                <p className="mt-2 text-sm text-mrn-wood-soft group-hover:text-mrn-sand leading-relaxed">
                  {a.desc}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-10 flex justify-center md:justify-start">
          <Dialog>
            <DialogTrigger asChild>
              <button
                data-testid="amenities-show-all"
                className="mrn-pill mrn-pill-outline"
              >
                <Plus size={16} strokeWidth={2} />
                Show all amenities
              </button>
            </DialogTrigger>
            <DialogContent
              data-testid="amenities-dialog"
              className="max-w-3xl bg-mrn-cream border-mrn-sand-dark/40 max-h-[85vh] overflow-y-auto"
            >
              <DialogHeader>
                <DialogTitle className="headline-display text-3xl md:text-4xl text-mrn-wood text-left">
                  Everything this place offers
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4 space-y-8">
                {ALL_AMENITIES.map((group) => (
                  <div key={group.category}>
                    <h4 className="text-xs uppercase tracking-[0.3em] text-mrn-terracotta font-medium">
                      {group.category}
                    </h4>
                    <ul className="mt-3 grid sm:grid-cols-2 gap-2 text-mrn-wood">
                      {group.items.map((it) => (
                        <li
                          key={it}
                          className="flex items-start gap-2 text-sm text-mrn-wood-soft border-b border-mrn-sand-dark/30 py-2"
                        >
                          <span className="text-mrn-terracotta mt-1">•</span>
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </section>
  );
}
